import React, { useState, useContext, useMemo } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  useMediaQuery,
} from "@mui/material";
import {
  Add,
  Collections,
  Save,
  Link,
  History,
  Delete,
  FileCopyOutlined,
  Folder,
} from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import { ProjectSchemeCreator } from "../projects_v2/projects_scheme/ProjectSchemeCreator";
import { useLoginState } from "../studioxr/auth/LoginState";
import {
  copyBoardFromId,
  deleteBoard,
} from "../v2/apis/boardsSchemeApi";
import {
  BoardSchemeInterface,
  CardTypes,
  ProjectSchemeInterface,
} from "ts-interfaces";
import { useTeamsProAccess } from "../hooks/useTeamsProAccess";
import {
  useMattoState,
  useProjectData,
  usePhysicalObjects,
} from "../MattoState";
import { useNavigate } from "react-router-dom";
import { AssociatedProjectModal } from "../projects_v2/components/AssociatedProjectModal";
import {
  archiveConfirmBody,
  DeleteComponentModal,
} from "../projects_v2/components/DeleteComponentModal";
import { processProjectData } from "../libs/projectUtils";
import { AuthContext } from "../auth/Auth";
import { getUrlFromRef } from "../libs/firebase";
import { cloudflareCDN, firebaseHelper, usePermissions } from "ui";
import { ProIconButton } from "../buttons/ProIconButton";
import { useProjectSchemas } from "../global-state/useProjectSchemas";
import { useBoards } from "../global-state/useBoards";
import { useEditorBoard } from "../global-state/useEditorBoard";
import { fileBoard } from "../v2/apis/accessApi";
import VersionHistoryModal from "../studioxr/collections/VersionHistoryModal";

export const FileMenu = ({ currentUser, createNewBoard }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const [createProjectScheme, setCreateProjectScheme] = useState(false);
  const [showAssociatedProjectModal, setShowAssociatedProjectModal] =
    useState(false);
  const [showDeleteBoardModal, setShowDeleteBoardModal] = useState(false);
  const isStateDirty = useMattoState((state) => state.isStateDirty);
  const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);
  const getPermissions = useLoginState((state) => state.getPermmissions);
  const projectData: any = useProjectData();
  const history = useNavigate();
  const { permissions } = usePermissions(currentUser?.uid, firebaseHelper.db);
  // Workspace-scoped Pro capability (follows the active workspace).
  const { isPro: isProUser } = useTeamsProAccess();
  const addProject = useProjectSchemas((state) => state.addProject);
  const invalidateCache = useProjectSchemas((state) => state.invalidateCache);

  // Enhance projectData with id field to match BoardSchemeInterface
  // This is necessary to ensure compatibility with the AssociatedProjectModal
  const enhancedProjectData: any = useMemo(() => {
    if (!projectData) return null;
    return {
      ...projectData,
      id: projectData.projectId,
    };
  }, [projectData]);
  // The stored board's team and owner, so filing follows Teams sharing.
  const editorBoard = useEditorBoard();
  const boardForFiling: any = useMemo(() => {
    if (!enhancedProjectData) return null;
    if (editorBoard.boardId !== enhancedProjectData.id) return enhancedProjectData;
    return {
      ...enhancedProjectData,
      user: editorBoard.owner,
      design_firm_id: editorBoard.firmId,
    };
  }, [enhancedProjectData, editorBoard]);

  // Version history states
  const [openDialog, setOpenDialog] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(false);

  const setPhysicalObjects = usePhysicalObjects(
    (state) => state.setPhysicalObjects,
  );
  const updateProjectData = useProjectData((state) => state.update);
  const { currentUser: authUser } = useContext(AuthContext);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyBoard = async (boardId: string, userId: string) => {
    try {
      if (isStateDirty == true) {
        const x = window.confirm(
          "Board unsaved! Are you sure you want to make a copy of the unsaved board? This will make you lose any unsaved changes.",
        );
        if (x != true) return;
      }
      setSnackbarMessage("Copying board...");
      const copiedBoard: BoardSchemeInterface = await copyBoardFromId(
        boardId,
        userId,
        await getPermissions(),
      );
      setSnackbarMessage("Board copied successfully");
      console.log("Board copied successfully:", copiedBoard);
      history("/home/boards");
    } catch (error) {
      console.error("Error copying board:", error);
    }
  };

  const handleDeleteBoard = async () => {
    try {
      // Check if user is authenticated before proceeding
      if (!currentUser?.uid) {
        console.error("User not authenticated");
        setSnackbarMessage("Please log in to archive boards");
        return;
      }

      if (!projectData.projectId) return;

      await deleteBoard(
        projectData.projectId,
        currentUser.uid,
        await getPermissions(),
      );
      history("/home/boards");
    } catch (e) {
      setSnackbarMessage(e instanceof Error ? e.message : "Couldn't archive the board");
    } finally {
      setShowDeleteBoardModal(false);
    }
  };

  const handleAssociatedProjectChange = async ({
    newProjectId,
    oldProjectId,
  }) => {
    // Check if user is authenticated before proceeding
    if (!currentUser?.uid) {
      console.error("User not authenticated");
      setSnackbarMessage("Please log in to associate projects");
      return;
    }

    try {
      // The stored board, not the editor's projectData: project stores show
      // its nested title and screenshot.
      const updatedBoard = await fileBoard(
        boardForFiling,
        newProjectId,
        currentUser.uid,
        permissions,
      );
      // SaveButton writes projectData.associatedProjectId back on every
      // (auto)save; without this the next save undoes the association.
      updateProjectData({ associatedProjectId: newProjectId ?? null });

      // If moving from one project to another
      if (oldProjectId && oldProjectId !== newProjectId) {
        useProjectSchemas.getState().removeBoardFromProjects(updatedBoard.id);
      }

      // Add to new project
      if (newProjectId) {
        useProjectSchemas.getState().updateBoardInProjects(updatedBoard);
      }

      // Update board store
      useBoards
        .getState()
        .setBoards(
          useBoards
            .getState()
            .boards.map((b) =>
              b.id === enhancedProjectData.id ? updatedBoard : b,
            ),
        );
      setShowAssociatedProjectModal(false);
      setSnackbarMessage("Board association updated successfully");
    } catch (error) {
      console.error("Error updating board association:", error);
      setSnackbarMessage(error.message || "Failed to update board association");
    }
  };

  const handleVersionHistory = async () => {
    if (!authUser?.uid) {
      setSnackbarMessage("Please log in to view version history");
      return;
    }

    if (!projectData.projectId) {
      setSnackbarMessage(
        "Please save the project first to view version history",
      );
      return;
    }

    setLoading(true);
    setOpenDialog(true);

    try {
      // Call getVersionHistory function
      const versionHistory = await firebaseHelper.getVersionHistory(
        "projects",
        projectData.projectId,
      );
      setVersions(versionHistory);
    } catch (error) {
      console.error("Error fetching version history:", error);
      setSnackbarMessage("Error loading version history");
    } finally {
      setLoading(false);
    }
  };

  const restoreVersion = async (version: any) => {
    setRestoringVersion(true);

    try {
      // Get the full version data
      const versionData = await firebaseHelper.getVersionById(
        "projects",
        projectData.projectId,
        version.versionId,
      );

      // Process the data using our shared utility function
      const processedData = processProjectData(versionData);

      // Update the physical objects state
      setPhysicalObjects(processedData.objects);

      // Update project data
      updateProjectData({
        ...versionData.projectData,
        ...{
          lighting: processedData.lighting,
          backgroundTexture: processedData.backgroundTexture,
          cameraPosition: processedData.cameraPosition,
          controlsTarget: processedData.controlsTarget,
          screenshot: processedData.screenshot,
        },
      });

      setOpenDialog(false);
      setSnackbarMessage("Version successfully restored");
    } catch (error) {
      console.error("Error restoring version:", error);
      setSnackbarMessage("Error restoring version");
    } finally {
      setRestoringVersion(false);
    }
  };

  const handleMenuItemClick = async (action) => {
    const message = "";
    // Implement your action handlers here
    switch (action) {
      case "create-board":
        createNewBoard();
        break;
      case "create-collection":
        useMattoState.getState().setModal({
          name: "collection",
          id: null,
          associatedProjectsList: [],
        });
        break;
      case "create-project":
        if (isStateDirty == true) {
          const x = window.confirm(
            "Board unsaved! This action will make you lose any unsaved changes.",
          );
          if (x != true) return;
        }
        setCreateProjectScheme(true);
        break;
      case "save-board":
        useMattoState.setState({ saveProjectNow: true });
        break;
      case "copy-board":
        if (!currentUser?.uid) {
          setSnackbarMessage("Please log in to copy boards");
          break;
        }
        if (projectData.projectId) {
          handleCopyBoard(projectData.projectId, currentUser.uid);
        } else {
          alert("Please save your board before making a copy.");
        }
        break;
      case "associate-project":
        setShowAssociatedProjectModal(true);
        break;
      case "version-history":
        handleVersionHistory();
        break;
      case "delete-board":
        setShowDeleteBoardModal(true);
        break;
      default:
        break;
    }
    handleClose();
  };

  const handleProjectCreated = (newProject: ProjectSchemeInterface) => {
    if (newProject) {
      addProject(newProject); // Add to store immediately
      history(`/home/projects/${newProject.id}`);
      return;
    }
    history("/home/projects");
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          paddingLeft: isXs ? 2 : 4,
          paddingRight: isXs ? 1 : 2,
          display: { xs: "none", sm: "none", md: "inline-block" },
        }}
      >
        <Button
          id="file-button"
          aria-controls={open ? "file-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          onClick={handleClick}
          startIcon={<MenuIcon />}
          sx={{ textTransform: "none", fontWeight: "bold" }}
        >
          File
        </Button>
        <Menu
          id="file-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "file-button",
          }}
          slotProps={{
            paper: {
              sx: {
                "& .MuiListItemText-root": { whiteSpace: "normal" },
                "& .MuiListItemIcon-root": { color: "black" },
                width: "340px",
              },
            },
          }}
        >
          <MenuItem onClick={() => handleMenuItemClick("create-board")}>
            <ListItemIcon>
              <Add fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create a New Board</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleMenuItemClick("create-collection")}>
            <ListItemIcon>
              <Collections fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create a New Collection</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => handleMenuItemClick("create-project")}
            disabled={!isProUser}
          >
            <ListItemIcon>
              <Folder fontSize="small" />
            </ListItemIcon>
            <ListItemText>Create a New Project</ListItemText>
            <ProIconButton id={enhancedProjectData?.id} size="32px" />
          </MenuItem>
          <Divider />

          <MenuItem onClick={() => handleMenuItemClick("save-board")}>
            <ListItemIcon>
              <Save fontSize="small" />
            </ListItemIcon>
            <ListItemText>Save this Board</ListItemText>
          </MenuItem>

          <MenuItem onClick={() => handleMenuItemClick("copy-board")}>
            <ListItemIcon>
              <FileCopyOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText>Make a Copy of this Board</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem
            onClick={() => handleMenuItemClick("associate-project")}
            disabled={!isProUser}
          >
            <ListItemIcon>
              <Link fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add/Change Associated Project</ListItemText>
            <ProIconButton id={enhancedProjectData?.id} size="32px" />
          </MenuItem>

          <Divider />

          <MenuItem onClick={() => handleMenuItemClick("version-history")}>
            <ListItemIcon>
              <History fontSize="small" />
            </ListItemIcon>
            <ListItemText>Version History</ListItemText>
          </MenuItem>

          <Divider />

          <MenuItem onClick={() => handleMenuItemClick("delete-board")}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ color: "error" }}>
              Archive this Board
            </ListItemText>
          </MenuItem>
        </Menu>
      </Stack>

      {createProjectScheme && (
        <ProjectSchemeCreator
          project={{}}
          setShowCreatorModal={setCreateProjectScheme}
          updateProject={handleProjectCreated}
          analyticsEntryPoint="board_file_menu"
        />
      )}
      {showAssociatedProjectModal && (
        <AssociatedProjectModal
          open={showAssociatedProjectModal}
          onClose={() => setShowAssociatedProjectModal(false)}
          onSave={handleAssociatedProjectChange}
          data={boardForFiling}
          type={CardTypes.Boards}
        />
      )}
      {showDeleteBoardModal && (
        <DeleteComponentModal
          open={showDeleteBoardModal}
          setOpen={setShowDeleteBoardModal}
          onDelete={handleDeleteBoard}
          title="Archive this board?"
          confirmLabel="Archive"
          body={archiveConfirmBody(projectData?.title || "Untitled Board")}
        />
      )}

      {/* Version History Modal */}
      <VersionHistoryModal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        versions={versions}
        loading={loading}
        restoringVersion={restoringVersion}
        onRestoreConfirmed={restoreVersion}
        cloudflareCDN={cloudflareCDN}
        getUrlFromRef={getUrlFromRef}
        collectionName={"projects"}
        recordId={projectData.projectId || ""}
      />
    </>
  );
};
